import { fetchProfile } from 'api/AccountApiService';
import { fetchUserSubscription } from 'api/SubscriptionApiService';
import Account from 'interface/Account';
import UserSubscription from 'interface/UserSubscription';
import React, { createContext, useState, useEffect, useContext, useRef } from 'react'
import logInfo from 'services/logService/infoLogger';
import clearJwtToken from 'utils/ClearJwtToken'
import getJwtToken from 'utils/GetJwtToken'
import usePromptLimitStore from 'store/usePromptLimitStore';
import { trackSessionEnd } from '../tracking';

/** When the signed-in user fails to load (a maintenance window, a network blip), try again with this growing pause. */
const PROFILE_RETRY_BASE_MS = 15_000
const PROFILE_RETRY_MAX_MS = 120_000
const PROFILE_RETRY_MAX_ATTEMPTS = 30


/**
 * Interface describing data the context should hold
 *
 * @interface AuthContextType
 * @typedef {AuthContextType}
 */
interface AuthContextType {
    /**
     * User object null if user is not logged in.
     *
     * @type {*}
     */
    user: Account | null;
    /**
     * Subscription object null if subscription is not available.
     */
    userSubscription: UserSubscription | null;
    /**
     * Destroys current session when user logs out.
     *
     * @type {() => void}
     */
    logout: () => void
    /**
     * Function to manually set user authentication status and update auth data.
     *
     * @param {boolean} auth - The new authentication status.
     */
    userAuthenticated: (auth: boolean) => void;
    /**
     * Re-fetches the user profile from the server and updates the user state.
     */
    refreshUser: () => Promise<void>;

    /**
     * Indicates if the authentication status is being determined.
     * 
     * @type {boolean}
     */
    loading: boolean;
}


/**
 * Description placeholder
 *
 * @type {*}
 */
const AuthContext = createContext<AuthContextType | null>(null)

/**
 * Description placeholder
 *
 * @export
 * @param {{ children: React.ReactNode}} param0 
 * @param {React.ReactNode} param0.children 
 * @returns {*} 
 */
export function AuthProvider({ children }: { children: React.ReactNode}) {
    const [user, setUser] = useState<Account | null >(null)
    const [userSubscription, setUserSubscription] = useState<UserSubscription | null>(null)
    const [authenticated, setAuthenticated] = useState<boolean>(false)
    const { setPromptLimit, resetClickCount } = usePromptLimitStore.getState();
    const [loading, setLoading] = useState<boolean>(true)
    const [retryTick, setRetryTick] = useState(0)
    const retryAttempts = useRef(0)
    
    /**
     * Custom function to handle token changes
     */
    const updateAuthData = () => {
        const authData = getJwtToken();
        setLoading(true);

        if (authData) {
            logInfo('Auth', { message: 'User authenticated' }, 'AuthContext')
            setAuthenticated(true)

            // Reset prompt limit and click count. Set prompt limit to -1 (no limit)
            setPromptLimit(-1)
            resetClickCount()

            Promise.all([
                fetchProfile('summary').catch(() => null),
                fetchUserSubscription().catch(() => null)
            ]).then(([accountResult, subscriptionResult]) => {
                setUser(accountResult as Account | null)
                setUserSubscription(subscriptionResult)
            }).finally(() => setLoading(false))
        } else {
            setUserSubscription(null)
            setAuthenticated(false)
            setUser(null);
            setLoading(false)
        }
    };

    const userAuthenticated = (auth: boolean) => {
        setAuthenticated(auth)
        updateAuthData()
    }

    const refreshUser = async (): Promise<void> => {
        const result = await fetchProfile('summary').catch(() => null)
        setUser(result as Account | null)
    }

    useEffect(() => {
        // Initial authentication check
        updateAuthData();

        window.addEventListener('authChange', updateAuthData);

        return () => {
            window.removeEventListener('authChange', updateAuthData);
        };
    }, [authenticated]);

    useEffect(() => {
        // A token without a loaded user makes the whole site look signed out (for example when the account
        // summary was refused during a maintenance window). Keep trying quietly so it recovers by itself.
        if (!authenticated || loading || !getJwtToken()) return
        if (user !== null) {
            retryAttempts.current = 0
            return
        }
        if (retryAttempts.current >= PROFILE_RETRY_MAX_ATTEMPTS) return

        const delay = Math.min(PROFILE_RETRY_BASE_MS * 2 ** retryAttempts.current, PROFILE_RETRY_MAX_MS)
        const retryLoad = async (): Promise<void> => {
            retryAttempts.current += 1
            const [account, subscription] = await Promise.all([
                fetchProfile('summary').catch(() => null),
                fetchUserSubscription().catch(() => null)
            ])
            if (subscription) setUserSubscription(subscription)
            if (account) setUser(account as Account)
            else setRetryTick((tick) => tick + 1)
        }
        const timer = setTimeout(() => { void retryLoad() }, delay)
        return () => clearTimeout(timer)
    }, [authenticated, loading, user, retryTick])

    const logout = () => {
        const controller = new AbortController()
        // End the tracking session while the token still exists. Not awaited: the request is
        // assembled synchronously (token and signature are captured now), and keepalive lets it
        // outlive the navigation below.
        void trackSessionEnd('signed_out')
        clearJwtToken()
        setUser(null)
        setAuthenticated(false)
        setPromptLimit(1) // Reset prompt limit to default
        resetClickCount() // Reset click count to 0

        controller.abort()
        window.location.href = '/login'
    }

    return (
        <AuthContext.Provider value={{ user, userSubscription, logout, userAuthenticated, refreshUser, loading }}>
            {children}
        </AuthContext.Provider>
    )
}

/**
 * Description placeholder
 *
 * @export
 * @returns {*} 
 */
export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}